import React, { FC, MutableRefObject, ReactElement, Ref, useContext, useEffect, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { Button, Menu, MenuItem } from '@material-ui/core'
import Icon, { IconName } from '../../../../React/Common/Components/Icon'
import { LocaleEntries, LocaleEntry } from '../../Molecules/LoginForm/Types/Types'
import { ReactState } from '../../../../React/Common/Types/Types'
import { LOCALE_EN_GB, resolveSupportedLocale } from '../../../../React/Common/Constants/Locale'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { getLocales } from '../../../../React/Common/API/ItemsGetters'
import { isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { displayError } from '../../../../React/Common/Utils/Messages'
import { getSessionLanguage, setSessionLanguage } from '../../../../React/Common/API/LanguageAPI'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../../../React/Common/Contexts'

type Props = {
    onChange: (locale: string) => void
}

export const LocaleSwitcher: FC<Props> = ({ onChange }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isLocaleOpen, setIsLocaleOpen]: ReactState<boolean> = useState<boolean>(false)
    const [availableLanguages, setAvailableLanguages]: ReactState<LocaleEntries> = useState<LocaleEntries>([
        {
            description: EMPTY_STRING,
            locale: LOCALE_EN_GB
        }
    ])
    const [selectedLocale, setSelectedLocale]: ReactState<string> = useState<string>(LOCALE_EN_GB)

    const anchorEl: Ref<HTMLButtonElement> = useRef<HTMLButtonElement>(null)

    const switchLocale = (locale: string): void => {
        if (isMounted.current) {
            const safeLocale: string = resolveSupportedLocale(locale)

            setSelectedLocale(safeLocale)
            onChange(safeLocale)
            setIsLocaleOpen(false)
            setSessionLanguage(safeLocale)
                .then((): void => {
                    language.setLanguage(safeLocale)
                    window.location.reload()
                })
                .catch(displayError)
        }
    }

    useEffect((): void => {
        getSessionLanguage().then((sessionLanguage: Nullable<string>): void => {
            if (isVarNotEmpty(sessionLanguage) && isMounted.current) {
                const safeLocale: string = resolveSupportedLocale(sessionLanguage)

                setSelectedLocale(safeLocale)
                onChange(safeLocale)
            }
        })

        getLocales()
            .then((locales: Nullable<LocaleEntries>): void => {
                if (isVarNotEmpty(locales) && isMounted.current) {
                    setAvailableLanguages(locales)
                }
            })
            .catch(displayError)
    }, [])

    return (
        <div className='locale-switcher'>
            <Button onClick={(): void => setIsLocaleOpen(true)} ref={anchorEl}>
                <>
                    <Icon iconName={IconName.ARROW_DROPDOWN} />
                    <Icon iconName={IconName.LANGUAGE} />
                </>
            </Button>
            <Menu open={isLocaleOpen} anchorEl={anchorEl.current} onClose={(): void => setIsLocaleOpen(false)}>
                {availableLanguages.map(
                    (availableLanguage: LocaleEntry): ReactElement => (
                        <MenuItem
                            key={availableLanguage.locale}
                            onClick={(): void => switchLocale(availableLanguage.locale)}
                        >
                            {availableLanguage.description}
                            {availableLanguage.locale === selectedLocale ? (
                                <Icon className='locale-active-icon' iconName={IconName.CHECK} />
                            ) : (
                                <></>
                            )}
                        </MenuItem>
                    )
                )}
            </Menu>
        </div>
    )
}
